/**
 * Reine Ableitungen fuer das „Gespraech als Mail-Fenster“ (docs/KONZEPT-GESPRAECH-MAILFENSTER.md,
 * Mockup V4): Aufteilung in Reiter, Initialen, Zustellstatus in Worten, Tagesbezeichnung, Vorschau.
 * Alles ohne React, damit es einzeln testbar ist (`test/gespraech-mailfenster.test.ts`).
 */
import { mailFehlerKurzfassung, EMAIL_STATUS_LABEL, istMailFehlgeschlagen, type MailEreignis } from "../ui/format";
import { sendezeitpunkt, type GespraechZeitQuelle } from "./gespraech";

export type MailFensterReiter = "gast" | "betrieb" | "verlauf";

/** Die Felder, die die Aufteilung braucht. `art` liefert der Server (GET /api/events/:id/emails);
 *  fehlt es (aelterer Stand, synthetische Nachricht), zaehlt die Zeile als Gespraech mit dem Gast. */
export interface MailFensterQuelle extends GespraechZeitQuelle {
  id: string;
  direction: "inbound" | "outbound";
  art?: "gast" | "betrieb";
}

/** Neueste zuerst nach ECHTER Sendezeit (lib/gespraech.ts), Gleichstand stabil. */
export function neuesteZuerst<T extends GespraechZeitQuelle>(zeilen: T[]): T[] {
  return [...zeilen].sort((a, b) => sendezeitpunkt(b).localeCompare(sendezeitpunkt(a)));
}

export function teileInReiter<T extends MailFensterQuelle>(emails: T[]): { gast: T[]; betrieb: T[] } {
  const gast: T[] = [];
  const betrieb: T[] = [];
  for (const e of emails) (e.art === "betrieb" ? betrieb : gast).push(e);
  return { gast: neuesteZuerst(gast), betrieb: neuesteZuerst(betrieb) };
}

/** „Gabriele Bauer“ -> „GB“, „STORIA“ -> „S“, leer -> „?“. */
export function initialen(name: string | null | undefined): string {
  const woerter = (name ?? "").replace(/<[^>]*>/g, " ").trim().split(/\s+/).filter(Boolean);
  if (woerter.length === 0) return "?";
  if (woerter.length === 1) return woerter[0]!.charAt(0).toUpperCase();
  return (woerter[0]!.charAt(0) + woerter[woerter.length - 1]!.charAt(0)).toUpperCase();
}

/** Anzeigename aus „Name <adresse>“ bzw. die nackte Adresse. */
export function anzeigeNameVon(adresse: string | null | undefined): string {
  const a = (adresse ?? "").trim();
  const m = a.match(/^"?([^"<]+?)"?\s*<[^>]+>$/);
  return (m ? m[1]! : a).trim();
}

/** Nackte Adresse aus „Name <adresse>“. */
export function adresseVon(adresse: string | null | undefined): string {
  const a = (adresse ?? "").trim();
  const m = a.match(/<([^<>]+)>/);
  return (m ? m[1]! : a).trim();
}

export type StatusTon = "ok" | "err" | "amb" | "neutral";
export interface MailStatus {
  ton: StatusTon;
  text: string;
}

interface StatusQuelle {
  direction: "inbound" | "outbound";
  status: string | null;
  error: string | null;
  ereignisse?: MailEreignis[];
  attachmentsManifest?: unknown[] | null;
}

function anhangText(n: number): string {
  return n === 1 ? "1 Anhang" : `${n} Anhänge`;
}

/**
 * Zustellstatus einer AUSGEHENDEN Mail in Worten (Listenzeile): gruen „Zugestellt · 2× geöffnet ·
 * 1 Anhang", rot „Nicht zugestellt · Postfach voll“, sonst neutral „Gesendet“. Eingehende Mails
 * haben keinen Zustellstatus (-> null; „Unbeantwortet“ kommt aus `unbeantwortetText`).
 */
export function mailStatus(m: StatusQuelle): MailStatus | null {
  if (m.direction !== "outbound") return null;
  if (istMailFehlgeschlagen(m.status)) {
    return { ton: "err", text: m.error ? `Nicht zugestellt · ${mailFehlerKurzfassung(m.error).replace(/\.$/, "")}` : "Nicht zugestellt" };
  }
  const ereignisse = m.ereignisse ?? [];
  const hat = (name: string) => ereignisse.find((e) => e.ereignis === name);
  const teile: string[] = [];
  const zugestellt = hat("delivered") != null || m.status === "delivered";
  const opened = hat("opened");
  const erstOffen = hat("unique_opened");
  const oeffnungen = opened ? Math.max(opened.anzahl, 1) : erstOffen ? 1 : 0;
  if (zugestellt) teile.push("Zugestellt");
  if (oeffnungen > 0) teile.push(`${oeffnungen}× geöffnet`);
  if (hat("click")) teile.push("geklickt");
  const anhaenge = m.attachmentsManifest?.length ?? 0;
  if (anhaenge > 0) teile.push(anhangText(anhaenge));
  if (zugestellt || oeffnungen > 0) return { ton: "ok", text: teile.join(" · ") };
  if (m.status === "deferred") return { ton: "amb", text: anhaenge > 0 ? `Verzögert · ${anhangText(anhaenge)}` : EMAIL_STATUS_LABEL.deferred! };
  if (m.status) {
    const basis = EMAIL_STATUS_LABEL[m.status] ?? m.status;
    return { ton: "neutral", text: anhaenge > 0 ? `${basis} · ${anhangText(anhaenge)}` : basis };
  }
  return anhaenge > 0 ? { ton: "neutral", text: anhangText(anhaenge) } : null;
}

/**
 * „Unbeantwortet seit 4 Std.“ — NUR fuer die juengste eingehende Gast-Nachricht, auf die danach
 * keine ausgehende Gast-Nachricht folgt. Liefert die Id dieser Nachricht (sonst null).
 */
export function unbeantworteteNachrichtId<T extends MailFensterQuelle>(gastNachrichten: T[]): string | null {
  const neueste = neuesteZuerst(gastNachrichten)[0];
  return neueste && neueste.direction === "inbound" ? neueste.id : null;
}

export function unbeantwortetText(seitIso: string, jetzt: number): string {
  const ms = jetzt - new Date(seitIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "Unbeantwortet";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `Unbeantwortet seit ${Math.max(min, 1)} Min.`;
  const std = Math.floor(min / 60);
  if (std < 24) return `Unbeantwortet seit ${std} Std.`;
  const tage = Math.floor(std / 24);
  return `Unbeantwortet seit ${tage} ${tage === 1 ? "Tag" : "Tagen"}`;
}

function tagStart(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** „Heute“, „Gestern“ oder „Montag, 19.10.2026“ (Tagestrenner der Liste). */
export function tagesBezeichnung(iso: string, jetzt: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diffTage = Math.round((tagStart(new Date(jetzt)) - tagStart(d)) / 86_400_000);
  if (diffTage === 0) return "Heute";
  if (diffTage === 1) return "Gestern";
  return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
}

export function tagesSchluessel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE");
}

/** Uhrzeit rechts in der Liste: „18:56“. */
export function uhrzeit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "–" : d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

/** „Dienstag, 20.10.2026 · 18:56“ (Kopf der geoeffneten Nachricht). */
export function langesDatum(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tag = d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "2-digit", year: "numeric" });
  return `${tag} · ${uhrzeit(iso)}`;
}

/** Kurzdatum fuer einzeilige Zeilen: „Mo., 19.10. 20:01“. */
export function kurzesDatum(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const tag = d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
  return `${tag} ${uhrzeit(iso)}`;
}

/** Vorschau: Leerraum zusammengezogen und gekuerzt (die Liste klemmt zusaetzlich auf zwei Zeilen). */
export function vorschau(text: string | null | undefined, max = 220): string {
  const t = (text ?? "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max).trimEnd()} …` : t;
}

/** „Re: …“ ohne Verdopplung („Re: AW: Re: x“ -> „Re: x“). */
export function antwortBetreff(betreff: string | null | undefined): string {
  const b = (betreff ?? "").replace(/^\s*((re|aw|fwd?|wg)\s*:\s*)+/i, "").trim();
  return b ? `Re: ${b}` : "Re: Ihre Nachricht";
}
